import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAPIAuthenticatedService } from '../../helpers/APICallsAuthenticated';
import { CacheKeys, ReferralWithScope_UserWithoutId } from '../../helpers/DataStructures';
import DateTimeFormat from '../../helpers/DateTimeFormat';
import { translateFromStatusToShortTextForOfferer } from '../../helpers/Language';
import Busy from '../../components/Busy';
import InHouseDialog from '../../components/InHouseDialog';
import ReferralRouteShell from './ReferralRouteShell';

const getDisplayName = (givenName?: string, familyName?: string, companyName?: string) => {
  if (companyName && companyName.trim() !== '') {
    return companyName;
  }

  return [givenName, familyName].filter(Boolean).join(' ');
};

interface AdminGroupReferralsPageProps {
  emptyMessage: string;
  errorMessage: string;
  queryKey: CacheKeys;
  title: string;
  loadReferrals: ReturnType<typeof useAPIAuthenticatedService>['getAdminGroupOneReferrals'];
}

const AdminGroupReferralsPage: React.FC<AdminGroupReferralsPageProps> = ({
  emptyMessage,
  errorMessage,
  queryKey,
  title,
  loadReferrals,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const apiServiceInstance = useAPIAuthenticatedService();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = `${location.pathname}${location.search}${location.hash}`;
  const [deleteCandidate, setDeleteCandidate] =
    useState<ReferralWithScope_UserWithoutId | null>(null);

  const { isLoading, isError, data, error } = useQuery([queryKey], loadReferrals, {
    keepPreviousData: true,
    retry: false,
  });

  const deleteReferralMutation = useMutation(
    (referralId: number) => apiServiceInstance.deleteAdminReferral(referralId),
    {
      onSuccess: (_response, referralId) => {
        // REQ-028: once backend confirms deletion, remove the row from the current admin list.
        queryClient.setQueryData<ReferralWithScope_UserWithoutId[] | undefined>(
          [queryKey],
          (current) => current?.filter((referral) => referral.id !== referralId)
        );
        setDeleteCandidate(null);
      },
    }
  );

  const resolvedErrorMessage =
    error instanceof Error && error.message ? error.message : errorMessage;
  const deleteErrorMessage =
    deleteReferralMutation.error instanceof Error
      ? deleteReferralMutation.error.message
      : 'Unable to delete referral.';

  const closeDeleteDialog = () => {
    if (deleteReferralMutation.isLoading) {
      return;
    }

    deleteReferralMutation.reset();
    setDeleteCandidate(null);
  };

  return (
    // REQ-026: admin referral tables keep their natural width and make the outer page card grow.
    <>
      <ReferralRouteShell backTo="/admins" fitContent maxWidth={false} title={title}>
        {isLoading ? (
          <Busy message="Loading referrals..." />
        ) : isError ? (
          <Typography>{resolvedErrorMessage}</Typography>
        ) : !data || data.length === 0 ? (
          <Typography>{emptyMessage}</Typography>
        ) : (
          <TableContainer component={Paper} sx={{ overflowX: 'visible' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Referrer</TableCell>
                  <TableCell>Business</TableCell>
                  {!isMobile && <TableCell>Contact</TableCell>}
                  {!isMobile && <TableCell>Offer</TableCell>}
                  <TableCell>Status</TableCell>
                  {!isMobile && <TableCell align="right">Fee</TableCell>}
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((referral: ReferralWithScope_UserWithoutId) => (
                  <TableRow key={referral.id}>
                    <TableCell>{referral.theReferralName}</TableCell>
                    <TableCell>
                      {getDisplayName(
                        referral.user.givenName,
                        referral.user.familyName,
                        referral.user.companyName
                      )}
                    </TableCell>
                    <TableCell>
                      {getDisplayName(
                        referral.offer.user.givenName,
                        referral.offer.user.familyName,
                        referral.offer.user.companyName
                      )}
                    </TableCell>
                    {!isMobile && <TableCell>{referral.theReferralContact}</TableCell>}
                    {!isMobile && (
                      <TableCell>{referral.offer.theOffer || 'Untitled offer'}</TableCell>
                    )}
                    <TableCell>{translateFromStatusToShortTextForOfferer(referral.status)}</TableCell>
                    {!isMobile && (
                      <TableCell align="right">{referral.offer.offeringFixedCashAmount}</TableCell>
                    )}
                    <TableCell>{DateTimeFormat(referral.created)}</TableCell>
                    <TableCell>
                      <Stack direction={isMobile ? 'column' : 'row'} spacing={1}>
                        {/* REQ-027: admin referral tables must open the shared conversation UI from either participant perspective. */}
                        <Button
                          color="primary"
                          data-testid={`admin-view-as-referrer-${referral.id}`}
                          onClick={() =>
                            navigate(`/admins/referrals/${referral.id}/as-referrer`, {
                              state: { backTo },
                            })
                          }
                          size="small"
                          variant="quiet"
                        >
                          View as referrer
                        </Button>
                        <Button
                          color="primary"
                          data-testid={`admin-view-as-offerer-${referral.id}`}
                          onClick={() =>
                            navigate(`/admins/referrals/${referral.id}/as-offerer`, {
                              state: { backTo },
                            })
                          }
                          size="small"
                          variant="quiet"
                        >
                          View as offerer
                        </Button>
                        <Button
                          color="secondary"
                          data-testid={`admin-delete-referral-${referral.id}`}
                          onClick={() => {
                            deleteReferralMutation.reset();
                            setDeleteCandidate(referral);
                          }}
                          size="small"
                          variant="quiet"
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ReferralRouteShell>

      <InHouseDialog
        closeLabel="Cancel"
        closeDisabled={deleteReferralMutation.isLoading}
        onClose={closeDeleteDialog}
        open={deleteCandidate !== null}
        title="Delete referral?"
        actions={
          <Button
            color="secondary"
            data-testid="admin-confirm-delete-referral"
            onClick={() => {
              if (deleteCandidate) {
                // REQ-028: use handled mutation state so failed admin deletes stay visible in the dialog.
                deleteReferralMutation.mutate(deleteCandidate.id);
              }
            }}
            disabled={deleteReferralMutation.isLoading}
            variant="quiet"
          >
            {deleteReferralMutation.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        }
      >
        <Stack spacing={1}>
          <Typography variant="h5">
            {deleteCandidate ? `Delete ${deleteCandidate.theReferralName}?` : ''}
          </Typography>
          {deleteReferralMutation.isError ? (
            <Typography color="error" variant="body2">
              {deleteErrorMessage}
            </Typography>
          ) : null}
        </Stack>
      </InHouseDialog>
    </>
  );
};

export default AdminGroupReferralsPage;
